import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';

export type ResellerTranscript = {
  session_id: string;
  filename: string;
  language: string | null;
  last_update: string | null;
  date_session_start?: string | null;
  duration?: number | null;
  is_deleted?: boolean;
  status: 'finished' | 'error' | string;
};

export type ResellerTranscriptResponse = {
  count: number;
  total_results: number;
  result: ResellerTranscript[];
};

export const useGetResellerTranscripts = (customerId: string) => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sort = (searchParams.sort as string | undefined) || undefined;
  const direction =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;
  const q = (searchParams.q as string | undefined) || undefined;

  const fetchResellerTranscripts =
    async (): Promise<ResellerTranscriptResponse> => {
      if (!customerId) throw new Error('customerId is required');
      const { resellerTranscriptsByCustomerId } = GET_URL_NEW();
      const url = resellerTranscriptsByCustomerId(customerId, {
        offset,
        limit,
        sort,
        direction,
        q
      });
      const { data } = await Axios.get<ResellerTranscriptResponse>(url);
      return data;
    };

  return useQuery<ResellerTranscriptResponse, AxiosError<BaseError>>(
    ['reseller-transcripts', customerId, offset, limit, q, sort, direction],
    fetchResellerTranscripts,
    { enabled: !!customerId, keepPreviousData: true }
  );
};
